import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, DownloadIcon, EyeIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, SearchIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock data
const campaignsData = [{
  id: 'CB-2023-001',
  name: 'Q3 Merchant Activation',
  code: 'ACT-Q3-23',
  status: 'active',
  frequency: 'Monthly',
  budget: 5000000,
  utilized: 2450000,
  startDate: '2023-07-01',
  endDate: '2023-09-30'
}, {
  id: 'CB-2023-002',
  name: 'Digital Week Bonanza',
  code: 'DIGI-WK-23',
  status: 'pending',
  frequency: 'Weekly',
  budget: 1000000,
  utilized: 0,
  startDate: '2023-10-01',
  endDate: '2023-10-07'
}, {
  id: 'CB-2023-003',
  name: 'Super Merchant Rewards',
  code: 'SUPER-23',
  status: 'draft',
  frequency: 'Quarterly',
  budget: 10000000,
  utilized: 0,
  startDate: '2023-10-01',
  endDate: '2023-12-31'
}, {
  id: 'CB-2023-004',
  name: 'New Year Kickoff',
  code: 'NY-24',
  status: 'rejected',
  frequency: 'Monthly',
  budget: 2000000,
  utilized: 0,
  startDate: '2024-01-01',
  endDate: '2024-01-31',
  rejectionReason: 'Budget allocation too high for Q1'
}];
interface Campaign {
  id: string;
  name: string;
  code: string;
  status: string;
  frequency: string;
  budget: number;
  utilized: number;
  startDate: string;
  endDate: string;
  rejectionReason?: string;
}
const CashbackCampaignList: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>(campaignsData);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const handleCreateCampaign = () => {
    navigate('/cashback/campaigns/new');
  };
  const handleEditCampaign = (id: string) => {
    navigate(`/cashback/campaigns/edit/${id}`);
  };
  const handleApproveClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowApprovalModal(true);
  };
  const handleRejectClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowRejectionModal(true);
  };
  const confirmApproval = () => {
    if (!selectedCampaign) return;
    const updatedCampaigns = campaigns.map(c => c.id === selectedCampaign.id ? {
      ...c,
      status: 'active'
    } : c);
    setCampaigns(updatedCampaigns);
    setShowApprovalModal(false);
    setSelectedCampaign(null);
  };
  const confirmRejection = () => {
    if (!selectedCampaign) return;
    const updatedCampaigns = campaigns.map(c => c.id === selectedCampaign.id ? {
      ...c,
      status: 'rejected',
      rejectionReason
    } : c);
    setCampaigns(updatedCampaigns);
    setShowRejectionModal(false);
    setRejectionReason('');
    setSelectedCampaign(null);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-3409" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending Approval" data-id="element-3410" />;
      case 'draft':
        return <StatusBadge status="default" text="Draft" data-id="element-3411" />;
      case 'rejected':
        return <StatusBadge status="danger" text="Rejected" data-id="element-3412" />;
      case 'expired':
        return <StatusBadge status="default" text="Expired" data-id="element-3413" />;
      case 'closed':
        return <StatusBadge status="info" text="Closed" data-id="element-3414" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-3415" />;
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? campaign.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  const columns = [{
    header: 'Campaign Name',
    accessor: (campaign: Campaign) => <div data-id="element-3416">
          <div className="font-medium text-gray-900 dark:text-white" data-id="element-3417">
            {campaign.name}
          </div>
          <div className="text-xs text-gray-500" data-id="element-3418">{campaign.code}</div>
        </div>,
    sortable: true
  }, {
    header: 'Status',
    accessor: (campaign: Campaign) => getStatusBadge(campaign.status),
    sortable: true
  }, {
    header: 'Frequency',
    accessor: 'frequency',
    sortable: true
  }, {
    header: 'Duration',
    accessor: (campaign: Campaign) => <div className="text-sm" data-id="element-3419">
          <div data-id="element-3420">{new Date(campaign.startDate).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500" data-id="element-3421">
            to {new Date(campaign.endDate).toLocaleDateString()}
          </div>
        </div>,
    sortable: true
  }, {
    header: 'Budget Utilization',
    accessor: (campaign: Campaign) => {
      const percentage = Math.round(campaign.utilized / campaign.budget * 100);
      return <div className="w-32" data-id="element-3422">
            <div className="flex justify-between text-xs mb-1" data-id="element-3423">
              <span data-id="element-3424">{percentage}%</span>
              <span className="text-gray-500" data-id="element-3425">
                {formatCurrency(campaign.utilized)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5" data-id="element-3426">
              <div className={`h-1.5 rounded-full ${percentage > 90 ? 'bg-red-500' : 'bg-blue-500'}`} style={{
            width: `${percentage}%`
          }} data-id="element-3427"></div>
            </div>
          </div>;
    },
    sortable: true
  }];
  return <div className="space-y-6" data-id="element-3428">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-3429">
        <div data-id="element-3430">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3431">
            Cashback Campaigns
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3432">
            Manage merchant incentive programs and cashback rules
          </p>
        </div>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-3433">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3434">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-3435" />
            Filters
          </button>
          <button onClick={handleCreateCampaign} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-3436">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-3437" />
            New Campaign
          </button>
        </div>
      </div>

      {showFilters && <Card data-id="element-3438">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3439">
            <div data-id="element-3440">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3441">
                Search
              </label>
              <div className="relative" data-id="element-3442">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3443">
                  <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-3444" />
                </div>
                <input type="text" className="w-full pl-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Search by name or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3445" />
              </div>
            </div>
            <div data-id="element-3446">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3447">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3448">
                <option value="" data-id="element-3449">All Statuses</option>
                <option value="active" data-id="element-3450">Active</option>
                <option value="pending" data-id="element-3451">Pending Approval</option>
                <option value="draft" data-id="element-3452">Draft</option>
                <option value="rejected" data-id="element-3453">Rejected</option>
              </select>
            </div>
          </div>
        </Card>}

      <Card data-id="element-3454">
        <Table columns={columns} data={filteredCampaigns} keyField="id" actions={campaign => <div className="flex justify-end space-x-2" data-id="element-3456">
              <button className="p-1 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-3457">
                <EyeIcon className="h-5 w-5" data-id="element-3458" />
              </button>

              {campaign.status === 'draft' && <button onClick={() => handleEditCampaign(campaign.id)} className="p-1 rounded-full text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-3459">
                  <EditIcon className="h-5 w-5" data-id="element-3460" />
                </button>}

              {campaign.status === 'pending' && <>
                  <button onClick={() => handleApproveClick(campaign)} className="p-1 rounded-full text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30" title="Approve" data-id="element-3461">
                    <CheckCircleIcon className="h-5 w-5" data-id="element-3462" />
                  </button>
                  <button onClick={() => handleRejectClick(campaign)} className="p-1 rounded-full text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30" title="Reject" data-id="element-3463">
                    <XCircleIcon className="h-5 w-5" data-id="element-3464" />
                  </button>
                </>}

              {['draft', 'rejected'].includes(campaign.status) && <button className="p-1 rounded-full text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-3465">
                  <TrashIcon className="h-5 w-5" data-id="element-3466" />
                </button>}
            </div>} data-id="element-3455" />
      </Card>

      {/* Approval Modal */}
      <Modal isOpen={showApprovalModal} onClose={() => setShowApprovalModal(false)} title="Approve Campaign" data-id="element-3467">
        <div className="space-y-4" data-id="element-3468">
          <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-3469">
            Are you sure you want to approve{' '}
            <strong data-id="element-3470">{selectedCampaign?.name}</strong>? This will activate the
            campaign engine for this configuration.
          </p>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-3471">
            <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50" data-id="element-3472">
              Cancel
            </button>
            <button onClick={confirmApproval} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-3473">
              Approve
            </button>
          </div>
        </div>
      </Modal>

      {/* Rejection Modal */}
      <Modal isOpen={showRejectionModal} onClose={() => setShowRejectionModal(false)} title="Reject Campaign" data-id="element-3474">
        <div className="space-y-4" data-id="element-3475">
          <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-3476">
            Please provide a reason for rejecting{' '}
            <strong data-id="element-3477">{selectedCampaign?.name}</strong>.
          </p>
          <div data-id="element-3478">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3479">
              Rejection Reason <span className="text-red-500" data-id="element-3480">*</span>
            </label>
            <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-red-500 focus:border-red-500" placeholder="Enter reason..." required data-id="element-3481" />
          </div>
          <div className="flex justify-end space-x-3 pt-4" data-id="element-3482">
            <button onClick={() => setShowRejectionModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50" data-id="element-3483">
              Cancel
            </button>
            <button onClick={confirmRejection} disabled={!rejectionReason.trim()} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50" data-id="element-3484">
              Reject
            </button>
          </div>
        </div>
      </Modal>
    </div>;
};
export default CashbackCampaignList;